/**
 * Table Component
 * 
 * Simple data table using design tokens.
 * Phase C - Library List UI
 */

import type { ReactNode } from 'react';

export interface TableColumn<T> {
  key: string;
  label: string;
  render: (item: T) => ReactNode;
  width?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  emptyMessage?: string;
  className?: string;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'No data available',
  className = '',
}: TableProps<T>) {
  if (data.length === 0) {
    return (
      <div className="text-center py-[var(--space-12)] text-[var(--surface-text-muted)]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`.trim()}>
      <table className="min-w-full divide-y divide-[var(--surface-border-subtle)]">
        <thead className="bg-[var(--surface-bg-subtle)]">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="px-[var(--space-6)] py-[var(--space-3)] text-left text-[var(--text-xs)] font-medium text-[var(--surface-text-secondary)] uppercase tracking-wider"
                style={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-[var(--surface-bg-panel)] divide-y divide-[var(--surface-border-subtle)]">
          {data.map((item) => (
            <tr key={keyExtractor(item)} className="hover:bg-[var(--surface-bg-subtle)]">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className="px-[var(--space-6)] py-[var(--space-4)] text-[var(--text-sm)] text-[var(--surface-text-primary)]"
                >
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
